/**
 * Email Activity — the global ledger of every email Pulse itself sends.
 *
 * 2026-10-28: one list across marketing (workflows, campaigns, follow-ups)
 * AND transactional mail (proposals, proposal reminders, CRM, client,
 * project, support, security, system). One row per message; click a row
 * for the exact body sent, the provider timeline and the recipient's
 * safety state. Transactional rows are listed for administrators only.
 * Externally sent Gmail/Outlook mail BCC'd into Pulse is NOT here — it
 * lives under each lead/client's "Externally Sent Emails".
 *
 * Single pane of glass for "did 200 actually go out today, which leads got
 * which step of which workflow, and is anything bouncing?" Filterable by
 * date range, workflow, campaign, status, step index, free-text recipient
 * search, and individual lead.
 *
 * Built per Mike's Jun-17-2026 ask: "I want to know every kind of email
 * that's going out of the system, segmented by category and campaign."
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  PaperPlaneTilt, ArrowsClockwise, Spinner, MagnifyingGlass,
  Envelope, Warning, CheckCircle, ClockClockwise, Lightning, Gauge,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import MarketingShell from "./MarketingShell";
import { formatLocal } from "@/lib/timezone";
import EmailActivityDetailDrawer, { engagementText } from "@/components/marketing/EmailActivityDetailDrawer";
import DigestOriginNote from "@/components/marketing/DigestOriginNote";
import {
  activityFiltersFromSearch, defaultActivityFilters, searchFromActivityFilters,
  selectOptions, STATUS_PRESETS, CATEGORY_PRESETS,
} from "@/lib/activityFilters";
import { appendOrigin, digestOriginFromSearch } from "@/lib/digestOrigin";
import { audienceLinkForEmails } from "@/lib/audienceFilters";

// Outcomes after which the address itself needs a decision (delete or
// retire the contact). Email Activity is the send ledger and is never
// edited; the decision is made on the Audience screen.
const FAILED_STATUSES = new Set(["bounced", "dropped", "spam", "blocked"]);

// Delivery tones come from the API (`row.delivery.tone`).
const DELIVERY_TONE = {
  ok:     "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  info:   "bg-[#EFF6FF] text-[#1E3A8A] border-[#BFDBFE]",
  warn:   "bg-[#FEF9C3] text-[#854D0E] border-[#FDE68A]",
  danger: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  muted:  "bg-[#FAFAFA] text-[#525252] border-[#E5E5E5]",
};

// Raw ledger statuses → plain English for the Status filter.
const STATUS_LABEL = {
  queued: "Queued", scheduled: "Scheduled", sent: "Accepted by provider",
  delivered: "Delivered", bounced: "Bounced / blocked", dropped: "Dropped",
  failed: "Failed — not sent", safe_mode_blocked: "Not sent (safe mode)",
  skipped_suppressed: "Blocked — address suppressed",
  demo_simulated: "Simulated (demo)", suppressed_preview_pod: "Not sent (preview)",
};

// Fallback when /filters has not loaded yet (or an older backend).
const DEFAULT_CATEGORIES = [
  { key: "WORKFLOW", label: "Workflow" }, { key: "CAMPAIGN", label: "Campaign" },
  { key: "FOLLOW_UP", label: "Marketing follow-up" },
];

const STATUS_TONE = {
  sent:      "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  delivered: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  opened:    "bg-[#EFF6FF] text-[#1E3A8A] border-[#BFDBFE]",
  clicked:   "bg-[#EFF6FF] text-[#1E3A8A] border-[#BFDBFE]",
  queued:    "bg-[#FEF9C3] text-[#854D0E] border-[#FDE68A]",
  scheduled: "bg-[#FEF9C3] text-[#854D0E] border-[#FDE68A]",
  bounced:   "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  dropped:   "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  blocked:   "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  spam:      "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  failed:    "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  deferred:  "bg-[#FAFAFA] text-[#525252] border-[#E5E5E5]",
};

const CATEGORY_TONE = {
  WORKFLOW:          "bg-[#EFF6FF] text-[#1E3A8A] border-[#BFDBFE]",
  CAMPAIGN:          "bg-[#FCE7F3] text-[#831843] border-[#FBCFE8]",
  FOLLOW_UP:         "bg-[#EFF6FF] text-[#1E3A8A] border-[#BFDBFE]",
  PROPOSAL:          "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  PROPOSAL_REMINDER: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  SECURITY:          "bg-[#FEF9C3] text-[#854D0E] border-[#FDE68A]",
};
const CATEGORY_TONE_DEFAULT = "bg-[#F3F4F6] text-[#374151] border-[#D1D5DB]";

const BAND_TONE = {
  green:    "bg-[#10B981]",
  amber:    "bg-[#F59E0B]",
  red:      "bg-[#EF4444]",
  starter:  "bg-[#6B7280]",
  override: "bg-[#0055FF]",
};

const PAGE_SIZE = 100;

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function MarketingActivityPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  // Filters — persisted in URL query string so a link to a filtered view is shareable.
  const [filters, setFilters] = useState(() => activityFiltersFromSearch(
    params.toString(), defaultActivityFilters(today(), daysAgo(7))));
  // Opened from a digest button: kept (and shown) until the operator
  // changes a filter, after which the email's count no longer applies.
  const [origin, setOrigin] = useState(() => digestOriginFromSearch(params.toString()));

  const [filterOptions, setFilterOptions] = useState({ workflows: [], campaigns: [], statuses: [], step_indexes: [], categories: [] });
  const [summary, setSummary] = useState(null);
  // Cap-override modal state (v1.69) — operator can adjust the daily
  // cap inline from the dispatcher tile without curl. The override
  // POST is audit-logged + auto-cancels the previous active row.
  const [capModalOpen, setCapModalOpen] = useState(false);
  const [capInput, setCapInput] = useState("200");
  const [capReason, setCapReason] = useState("");
  const [capSubmitting, setCapSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  // A failed load is shown as an error with Retry — never as "no emails".
  const [loadError, setLoadError] = useState(null);
  const [txnVisible, setTxnVisible] = useState(true);
  const [visibleNote, setVisibleNote] = useState(null);
  // Detail drawer overlays the list, so filters / page / scroll survive.
  const [detailId, setDetailId] = useState(null);
  const closeDetail = useCallback(() => setDetailId(null), []);

  // Load filter options once.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/marketing/activity/filters");
        setFilterOptions(r.data);
      } catch (e) {
        toast.error("Failed to load filter options");
      }
    })();
  }, []);

  const buildQuery = useCallback(() => {
    const qp = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) qp.set(k, v);
    });
    qp.set("limit", String(PAGE_SIZE));
    qp.set("offset", String(offset));
    return qp.toString();
  }, [filters, offset]);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // The dispatcher tiles are marketing-only: a viewer without the
      // Campaigns permission gets 403 there and simply sees no tiles.
      const [feed, sum] = await Promise.all([
        api.get(`/marketing/activity?${buildQuery()}`),
        api.get("/marketing/activity/summary").catch(() => ({ data: null })),
      ]);
      setRows(feed.data.rows || []);
      setTotal(feed.data.total || 0);
      setTxnVisible(feed.data.transactional_visible !== false);
      setVisibleNote(feed.data.visibility_note || null);
      setSummary(sum.data);
      // Mirror filters into URL so the operator can bookmark/share the view.
      setParams(appendOrigin(searchFromActivityFilters(filters), origin), { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setLoadError(typeof detail === "string" && detail ? detail : "The email list could not be loaded.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildQuery, filters, origin, setParams]);

  useEffect(() => { loadFeed(); /* eslint-disable-next-line */ }, [offset, filters]);

  // (Auto-refresh polling removed pending a clean hook pattern that
  // doesn't trip react-hooks/set-state-in-effect. The Refresh button
  // is the source of truth for now — Mike can click it.)

  const update = (k, v) => { setOffset(0); setOrigin(null); setFilters(f => ({ ...f, [k]: v })); };
  const reset = () => {
    setOffset(0);
    setOrigin(null);
    setFilters(defaultActivityFilters(today(), daysAgo(7)));
  };

  const capPct = summary?.cap?.value
    ? Math.min(100, (summary.today.total / summary.cap.value) * 100)
    : 0;

  return (
    <MarketingShell>
      <div className="px-4 py-3 w-full space-y-3" data-testid="marketing-activity-page">
        {/* Compact header row — title + description + refresh inline */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-black tracking-tight text-[#0A0A0A] flex items-center gap-2">
              <Lightning size={20} weight="bold" /> Email Activity
            </h1>
            <p className="text-[11px] text-[#A3A3A3] leading-snug hidden lg:block">
              Every email Pulse sends — workflows, campaigns, proposals, CRM, client and system mail. Click a row for the exact email sent.
            </p>
          </div>
          <button onClick={loadFeed} disabled={loading}
                  data-testid="activity-refresh"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#E5E5E5] hover:border-[#0055FF] hover:text-[#0055FF] text-xs">
            {loading ? <Spinner size={12} className="animate-spin"/> : <ArrowsClockwise size={12}/>}
            Refresh
          </button>
        </div>

        {/* Compact top strip — tiles inline with the filter bar on wide screens */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="activity-tiles">
            <Tile icon={Envelope} label="Marketing sent today" value={summary.today.sent}
                  hint={`${summary.today.bounced} bounced · ${summary.today.failed} failed`} />
            <Tile icon={ClockClockwise} label="Marketing queued today" value={summary.today.queued}
                  hint={`${summary.backlog_due_now} due now`} />
            <Tile icon={Gauge} label="Daily cap"
                  value={`${summary.today.total} / ${summary.cap.value}`}
                  hint={`${summary.cap.band || "—"} · ${summary.cap.headroom} headroom · click to override`}
                  bar={{ pct: capPct, tone: BAND_TONE[summary.cap.band] || "bg-[#0055FF]" }}
                  onClick={() => {
                    setCapInput(String(summary.cap.value || 200));
                    setCapReason("");
                    setCapModalOpen(true);
                  }}
                  testid="activity-tile-cap" />
            <Tile icon={CheckCircle} label="7-day total"
                  value={(summary.last_7d || []).reduce((s,r) => s + (r.count || 0), 0)}
                  hint={(summary.last_7d || []).map(r => r.count).join(" · ")}
                  hintTitle={(summary.last_7d || []).map(r => `${r.date}: ${r.count}`).join("\n")} />
          </div>
        )}

        <DigestOriginNote origin={origin} current={loading ? null : total} />

        {/* Single-row horizontal filter bar */}
        <div className="border border-[#E5E5E5] bg-white px-3 py-2" data-testid="activity-filters">
          <div className="flex items-end gap-2 flex-wrap">
            <Field label="From">
              <input type="date" value={filters.date_from} onChange={e => update("date_from", e.target.value)}
                     data-testid="activity-filter-from"
                     className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-32"/>
            </Field>
            <Field label="To">
              <input type="date" value={filters.date_to} onChange={e => update("date_to", e.target.value)}
                     data-testid="activity-filter-to"
                     className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-32"/>
            </Field>
            <Field label="Workflow">
              <select value={filters.workflow_id} onChange={e => update("workflow_id", e.target.value)}
                      data-testid="activity-filter-workflow"
                      className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-48">
                <option value="">All</option>
                {filterOptions.workflows.map(w =>
                  <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </Field>
            <Field label="Campaign">
              <select value={filters.campaign_id} onChange={e => update("campaign_id", e.target.value)}
                      data-testid="activity-filter-campaign"
                      className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-44">
                <option value="">All</option>
                {filterOptions.campaigns.map(c =>
                  <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Step #">
              <select value={filters.step_index} onChange={e => update("step_index", e.target.value)}
                      data-testid="activity-filter-step"
                      className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-28">
                <option value="">All</option>
                {filterOptions.step_indexes.map(s =>
                  <option key={s} value={s}>Email {s + 1}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={filters.status} onChange={e => update("status", e.target.value)}
                      data-testid="activity-filter-status"
                      className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-40">
                <option value="">All</option>
                {selectOptions(filters.status, STATUS_PRESETS, filterOptions.statuses, s => STATUS_LABEL[s] || s).map(o =>
                  <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Type">
              <select value={filters.category} onChange={e => update("category", e.target.value)}
                      data-testid="activity-filter-category"
                      className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-44">
                <option value="all">All types</option>
                {(() => {
                  const cats = filterOptions.categories?.length ? filterOptions.categories : DEFAULT_CATEGORIES;
                  const label = Object.fromEntries(cats.map(c => [c.key, c.label]));
                  return selectOptions(filters.category, CATEGORY_PRESETS, cats.map(c => c.key), k => label[k] || k);
                })().map(o =>
                  <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Search">
              <div className="relative">
                <MagnifyingGlass size={10} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                <input type="text" value={filters.search} onChange={e => update("search", e.target.value)}
                       placeholder="email / name / company / subject"
                       data-testid="activity-filter-search"
                       className="border border-[#E5E5E5] pl-5 pr-1.5 py-0.5 text-xs w-56" />
              </div>
            </Field>
            <Field label="Lead ID">
              <input type="text" value={filters.lead_id} onChange={e => update("lead_id", e.target.value)}
                     placeholder="lead_…"
                     data-testid="activity-filter-lead"
                     className="border border-[#E5E5E5] px-1.5 py-0.5 text-xs w-36 font-mono" />
            </Field>
            <button onClick={reset} className="text-[10px] underline text-[#525252] hover:text-[#0055FF] pb-1 ml-auto"
                    data-testid="activity-filter-reset">Reset</button>
          </div>
        </div>

        {/* Failed deliveries -> the screen where the contacts can be cleaned up */}
        {(() => {
          const failed = rows.filter(r => FAILED_STATUSES.has((r.status || "").toLowerCase()) && r.to_email);
          const href = audienceLinkForEmails(failed.map(r => r.to_email));
          if (!href) return null;
          const n = new Set(failed.map(r => r.to_email.toLowerCase())).size;
          return (
            <div className="flex flex-wrap items-center gap-2 border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-xs text-[#991B1B]" data-testid="activity-cleanup-bar">
              <Warning size={14} className="shrink-0" />
              <span>
                {n.toLocaleString()} failed address{n === 1 ? "" : "es"} in this view. This list is the send history and is kept;
                delete or retire the contacts on the Audience screen.
              </span>
              <button onClick={() => navigate(href)} className="ml-auto px-2 py-1 bg-[#DC2626] text-white font-bold uppercase tracking-wider text-[10px]"
                      data-testid="activity-cleanup-open">
                Review {n === 1 ? "this contact" : `these ${n.toLocaleString()} contacts`} for cleanup
              </button>
            </div>
          );
        })()}

        {/* Table */}
        <div className="border border-[#E5E5E5] bg-white">
          <div className="px-3 py-1.5 border-b border-[#E5E5E5] bg-[#FAFAFA] flex items-center justify-between">
            <div className="text-[11px] text-[#525252]">
              <span className="font-bold text-[#0A0A0A]">{total.toLocaleString()}</span> matching · showing
              <span className="font-bold text-[#0A0A0A] ml-1">{offset + 1}–{Math.min(offset + rows.length, total)}</span>
            </div>
            <div className="flex gap-1">
              <button disabled={offset === 0 || loading}
                      onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                      data-testid="activity-prev"
                      className="px-2 py-0.5 text-[11px] border border-[#E5E5E5] disabled:opacity-40">Prev</button>
              <button disabled={offset + rows.length >= total || loading}
                      onClick={() => setOffset(offset + PAGE_SIZE)}
                      data-testid="activity-next"
                      className="px-2 py-0.5 text-[11px] border border-[#E5E5E5] disabled:opacity-40">Next</button>
            </div>
          </div>

          {loading && rows.length === 0 && (
            <div className="p-6 flex justify-center" data-testid="activity-loading">
              <Spinner size={24} className="animate-spin text-[#0055FF]"/>
            </div>
          )}

          {(visibleNote || !txnVisible) && (
            <div className="px-3 py-1.5 border-b border-[#E5E5E5] text-[10px] text-[#737373]" data-testid="activity-txn-note">
              {visibleNote || "Showing marketing emails only."}
            </div>
          )}

          {!loading && loadError && (
            <div className="m-3 p-3 border border-[#FECACA] bg-[#FEF2F2] text-xs text-[#991B1B]" role="alert" data-testid="activity-error">
              <div className="flex items-start gap-2">
                <Warning size={14} className="mt-0.5 shrink-0" />
                <span>Could not load email activity. {loadError} Please try again; if this continues, contact support.</span>
              </div>
              <button onClick={loadFeed} className="mt-2 px-2 py-1 border border-[#991B1B] font-bold" data-testid="activity-retry">Retry</button>
            </div>
          )}

          {!loading && !loadError && rows.length === 0 && (
            <div className="p-6 text-center" data-testid="activity-empty">
              <Envelope size={20} className="text-[#A3A3A3] mx-auto mb-1"/>
              <p className="text-xs font-bold text-[#525252]">No emails match the current filters.</p>
              <button onClick={reset} className="mt-1 text-[11px] underline text-[#0055FF]">Reset filters</button>
            </div>
          )}

          {rows.length > 0 && (
            <table className="w-full text-xs" data-testid="activity-table">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5] text-[9px] font-bold uppercase tracking-wider text-[#525252]">
                <tr>
                  <th className="px-2 py-1.5 text-left">Sent</th>
                  <th className="px-2 py-1.5 text-left">Recipient</th>
                  <th className="px-2 py-1.5 text-left">Type</th>
                  <th className="px-2 py-1.5 text-left">Subject</th>
                  <th className="px-2 py-1.5 text-left">Delivery</th>
                  <th className="px-2 py-1.5 text-left" title="Human opens. Scanner/bot opens are excluded.">Opened</th>
                  <th className="px-2 py-1.5 text-left" title="Human clicks. Scanner/bot clicks are excluded.">Clicked</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}
                      className={`border-b border-[#F5F5F5] cursor-pointer hover:bg-[#F0F6FF] focus:outline-none focus:bg-[#F0F6FF] ${detailId === r.id ? "bg-[#F0F6FF]" : ""}`}
                      onClick={() => setDetailId(r.id)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailId(r.id); } }}
                      tabIndex={0}
                      role="button"
                      aria-label={`Open email details for ${r.to_email || "recipient"}`}
                      title="Click for what was sent, delivery events and recipient status"
                      data-testid={`activity-row-${r.id}`}>
                    <td className="px-2 py-1.5 align-top text-[10px] text-[#525252] whitespace-nowrap">
                      {r.sent_at ? formatLocal(r.sent_at) : (
                        <span title="Not handed to the provider">{r.queued_at ? `Queued ${formatLocal(r.queued_at)}` : "—"}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <div className="font-bold text-[#0A0A0A] text-xs leading-tight">{r.recipient_name || r.contact_name || "—"}</div>
                      <div className="text-[10px] text-[#525252] leading-tight">{r.to_email}</div>
                      {FAILED_STATUSES.has((r.status || "").toLowerCase()) && r.to_email && (
                        <button onClick={(e) => { e.stopPropagation(); navigate(audienceLinkForEmails([r.to_email])); }}
                                className="text-[10px] text-[#0055FF] underline leading-tight"
                                data-testid={`activity-cleanup-${r.id}`}>
                          Clean up contact
                        </button>
                      )}
                      {r.company_name && <div className="text-[10px] text-[#A3A3A3] italic leading-tight">{r.company_name}</div>}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <span className={`inline-block px-1 py-0 text-[9px] font-bold uppercase tracking-wider border ${CATEGORY_TONE[r.category] || CATEGORY_TONE_DEFAULT}`}>
                        {r.category_label || r.category}
                      </span>
                      <div className="text-[10px] text-[#525252] leading-tight mt-0.5 max-w-[180px] truncate"
                           title={r.workflow_name || r.campaign_name || ""}>
                        {r.workflow_name || r.campaign_name || (r.proposal_number ? `Proposal ${r.proposal_number}` : "")}
                        {r.step_label ? ` · ${r.step_label}` : ""}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 align-top text-[11px] text-[#0A0A0A] max-w-md truncate"
                        title={r.subject || ""}>
                      {r.subject || <span className="italic text-[#A3A3A3]">subject not retained</span>}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <span className={`inline-block px-1 py-0 text-[9px] font-bold uppercase tracking-wider border ${
                              DELIVERY_TONE[r.delivery?.tone] || STATUS_TONE[r.status] || DELIVERY_TONE.muted}`}
                            data-testid={`activity-delivery-${r.id}`}>
                        {r.delivery?.label || r.status}
                      </span>
                      {r.error_message && (
                        <div className="text-[9px] text-[#991B1B] leading-tight mt-0.5 max-w-xs truncate"
                             title={r.error_message}>
                          <Warning size={9} className="inline -mt-0.5 mr-0.5"/>
                          {r.error_message.slice(0, 60)}
                        </div>
                      )}
                    </td>
                    <EngagementCell value={r.opened} testid={`activity-opened-${r.id}`} />
                    <EngagementCell value={r.clicked} testid={`activity-clicked-${r.id}`} />
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Cap-override modal (v1.69) */}
        {capModalOpen && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
               onClick={() => !capSubmitting && setCapModalOpen(false)}
               data-testid="cap-override-modal-overlay">
            <div className="bg-white border border-[#E5E5E5] max-w-md w-full p-5 space-y-3"
                 onClick={e => e.stopPropagation()}>
              <div>
                <h2 className="text-lg font-black text-[#0A0A0A] flex items-center gap-2">
                  <Gauge size={18} weight="bold"/> Override daily send cap
                </h2>
                <p className="text-xs text-[#525252] mt-1 leading-snug">
                  Sets a manual override on `mkt_cap_overrides`. The adaptive
                  bounce-rate kill switches still apply on top (amber → 150,
                  red → 0). Audit-logged. Replaces any active override.
                </p>
                {summary && (
                  <p className="text-[11px] text-[#A3A3A3] mt-2">
                    Current cap: <span className="font-bold text-[#0A0A0A]">{summary.cap.value}</span>
                    {" "}· band: <span className="font-bold text-[#0A0A0A]">{summary.cap.band || "—"}</span>
                    {" "}· today: <span className="font-bold text-[#0A0A0A]">{summary.today.total}</span> sent
                  </p>
                )}
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">New daily cap</label>
                <input type="number" min="1" max="10000" step="1"
                       value={capInput} onChange={e => setCapInput(e.target.value)}
                       disabled={capSubmitting}
                       data-testid="cap-override-input"
                       className="border border-[#E5E5E5] px-2 py-1.5 text-sm w-full mt-1"/>
                <div className="flex gap-1 mt-1.5">
                  {[100, 200, 300, 500].map(n => (
                    <button key={n} type="button" disabled={capSubmitting}
                            onClick={() => setCapInput(String(n))}
                            className={`px-2 py-0.5 text-[10px] font-bold border ${
                              String(n) === capInput
                                ? "border-[#0055FF] bg-[#EFF6FF] text-[#0055FF]"
                                : "border-[#E5E5E5] text-[#525252] hover:border-[#0055FF]"
                            }`}>
                      {n}/day
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">Reason (audit trail)</label>
                <textarea value={capReason} onChange={e => setCapReason(e.target.value)}
                          rows={2} disabled={capSubmitting}
                          placeholder="e.g. drain Reactivate-cold-leads backlog"
                          data-testid="cap-override-reason"
                          className="border border-[#E5E5E5] px-2 py-1.5 text-xs w-full mt-1"/>
              </div>
              <div className="flex justify-between items-center gap-2 pt-1">
                <button onClick={async () => {
                          if (capSubmitting) return;
                          if (!window.confirm("Clear the override and revert to the adaptive band?")) return;
                          setCapSubmitting(true);
                          try {
                            await api.delete("/marketing/cap-override");
                            toast.success("Override cleared. Adaptive band rules now apply.");
                            setCapModalOpen(false);
                            loadFeed();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Failed to clear override");
                          } finally { setCapSubmitting(false); }
                        }}
                        disabled={capSubmitting}
                        data-testid="cap-override-clear"
                        className="text-[11px] underline text-[#991B1B] hover:text-[#7F1D1D] disabled:opacity-40">
                  Clear override
                </button>
                <div className="flex gap-2">
                  <button onClick={() => setCapModalOpen(false)} disabled={capSubmitting}
                          data-testid="cap-override-cancel"
                          className="px-3 py-1.5 border border-[#E5E5E5] hover:border-[#0055FF] hover:text-[#0055FF] text-xs">
                    Cancel
                  </button>
                  <button onClick={async () => {
                            const cap = parseInt(capInput, 10);
                            if (!cap || cap < 1 || cap > 10000) {
                              toast.error("Cap must be between 1 and 10000");
                              return;
                            }
                            setCapSubmitting(true);
                            try {
                              const r = await api.post("/marketing/cap-override", {
                                daily_cap: cap, reason: capReason || null,
                              });
                              toast.success(`Override set: ${r.data.daily_cap}/day · ${r.data.id}`);
                              setCapModalOpen(false);
                              loadFeed();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Failed to set override");
                            } finally { setCapSubmitting(false); }
                          }}
                          disabled={capSubmitting}
                          data-testid="cap-override-submit"
                          className="px-3 py-1.5 bg-[#0055FF] hover:bg-[#003ECC] text-white text-xs font-bold">
                    {capSubmitting ? "Saving…" : "Set override"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {detailId && <EmailActivityDetailDrawer sendId={detailId} onClose={closeDetail} />}
    </MarketingShell>
  );
}

// Opened / Clicked: "Yes" / "3x" / "No" / "Not tracked" ("—" when the
// email never left Pulse). Never "No" without tracking evidence.
const EngagementCell = ({ value, testid }) => {
  const text = engagementText(value);
  const tone = value?.state === "yes" ? "text-[#065F46] font-bold"
    : value?.state === "not_tracked" || value?.state === "internal" ? "text-[#A3A3A3] italic" : "text-[#525252]";
  const tip = value?.note
    || (value?.state === "not_tracked" ? "No open/click tracking evidence exists for this email." : "")
    || (value?.state === "internal" ? "Opened/clicked by Eyvo staff (internal recipient) — not recipient engagement." : "")
    || (value?.bot_count ? `${value.bot_count} scanner/bot event(s) excluded` : "");
  return (
    <td className={`px-2 py-1.5 align-top text-[11px] whitespace-nowrap ${tone}`} title={tip} data-testid={testid}>
      {text}
      {value?.basis === "proposal_read_receipt" && value?.state === "yes" && <span className="text-[9px] text-[#A3A3A3]"> (receipt)</span>}
    </td>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-0.5">
    <label className="text-[8px] font-bold uppercase tracking-wider text-[#A3A3A3] leading-none">{label}</label>
    {children}
  </div>
);

const Tile = ({ icon: Icon, label, value, hint, hintTitle, bar, testid, onClick }) => {
  const interactive = typeof onClick === "function";
  return (
    <div
      className={`border border-[#E5E5E5] bg-white px-2.5 py-1.5 ${interactive ? "cursor-pointer hover:border-[#0055FF] hover:bg-[#F0F7FF] transition-colors" : ""}`}
      data-testid={testid}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-[#525252]">
        <Icon size={10} weight="bold"/> {label}
      </div>
      <div className="text-lg font-black tracking-tight text-[#0A0A0A] leading-tight mt-0.5">{value ?? "—"}</div>
      {hint && (
        <div className="text-[9px] text-[#A3A3A3] leading-tight" title={hintTitle || ""}>{hint}</div>
      )}
      {bar && (
        <div className="mt-1 h-1 bg-[#F5F5F5] rounded overflow-hidden">
          <div className={`h-full ${bar.tone}`} style={{ width: `${bar.pct}%` }}/>
        </div>
      )}
    </div>
  );
};
